import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { countryFieldHtml, wireAddressFields, addressPasteFieldHtml } from '../components/PropertyBasicsFields.js';

// Dedicated create/edit page for a reusable tenant party (the Contacts list).
// Reached at /tenants/new (create) and /tenants/:id/edit (edit); the list view
// (TenantsView) links here instead of opening an inline panel. Entity type is a
// tile picker mirroring the property unit-type picker, and the address block
// reuses the property address helpers (country select + postal→city autofill).
const ENTITY_TYPES = [
  { key: 'individual', icon: 'user' },
  { key: 'company', icon: 'building' },
];

export class TenantFormView extends Component {
  constructor(ctx = null) {
    super();
    const params = ctx && ctx.params ? ctx.params : {};
    this.id = params.id || null; // edit when a party id is present
    this.draft = { entity_type: 'individual' };
    this.state = { loading: !!this.id, saving: false, error: null, formError: null };
  }

  template() {
    const back = `<a href="#/tenants" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('tenants.form.back'))}</span></a>`;
    if (this.state.loading) {
      return `
        <section class="stack">
          ${back}
          ${this.state.error
            ? `<div class="error-banner">${esc(this.state.error)}</div>`
            : `<div class="muted">${esc(t('tenants.loading'))}</div>`}
        </section>`;
    }
    const p = this.draft;
    const isCompany = (p.entity_type || 'individual') === 'company';
    const title = this.id ? t('tenants.form.edit_title') : t('tenants.form.create_title');
    const tile = (e) => {
      const sel = (p.entity_type || 'individual') === e.key;
      return `
        <button type="button" class="unit-type-tile${sel ? ' is-selected' : ''}"
                role="radio" aria-checked="${sel ? 'true' : 'false'}" data-entity-pick="${esc(e.key)}">
          <span class="unit-type-tile-icon">${icon(e.icon)}</span>
          <span class="unit-type-tile-label">${esc(t('tenants.entity.' + e.key))}</span>
        </button>`;
    };
    return `
      <section class="stack">
        ${back}
        <form class="panel" data-tenant-form>
          <div class="panel-body stack">
            <h2 style="margin:0">${esc(title)}</h2>
            ${this.state.formError ? `<div class="error-banner">${esc(this.state.formError)}</div>` : ''}
            <div class="field"><label>${esc(t('tenants.form.entity_type'))}</label>
              <div class="unit-type-tiles" data-entity-picker role="radiogroup" aria-label="${esc(t('tenants.form.entity_type'))}">
                ${ENTITY_TYPES.map(tile).join('')}
              </div>
              <input type="hidden" name="entity_type" value="${esc(p.entity_type || 'individual')}" />
            </div>

            <h3 style="margin:var(--space-2) 0 0">${esc(t('tenants.form.identity_section'))}</h3>
            <label class="stack-2"><span>${esc(t('tenants.form.display_name'))}</span>
              <input class="input" name="display_name" value="${esc(p.display_name || '')}" required maxlength="190" /></label>
            <div class="field-row" style="grid-template-columns:1fr 1fr;">
              <label class="stack-2"><span>${esc(t('tenants.form.email'))}</span>
                <input class="input" name="email" type="email" value="${esc(p.email || '')}" maxlength="190" /></label>
              <label class="stack-2"><span>${esc(t('tenants.form.phone'))}</span>
                <input class="input" name="phone" value="${esc(p.phone || '')}" maxlength="64" /></label>
            </div>
            <label class="stack-2"><span>${esc(t('tenants.form.tax_number'))}</span>
              <input class="input" name="tax_number" value="${esc(p.tax_number || '')}" maxlength="64" /></label>
            <div data-company-fields ${isCompany ? '' : 'hidden'} class="stack">
              <label class="stack-2"><span>${esc(t('tenants.form.company_reg'))}</span>
                <input class="input" name="company_reg" value="${esc(p.company_reg || '')}" maxlength="64" /></label>
              <label class="stack-2"><span>${esc(t('tenants.form.eu_tax_number'))}</span>
                <input class="input" name="eu_tax_number" value="${esc(p.eu_tax_number || '')}" maxlength="64" /></label>
            </div>

            <h3 style="margin:var(--space-2) 0 0">${esc(t('tenants.form.address_section'))}</h3>
            ${addressPasteFieldHtml()}
            <div class="field-row" style="grid-template-columns:160px 1fr;">
              <label class="stack-2"><span>${esc(t('tenants.form.postal_code'))}</span>
                <input class="input" name="postal_code" maxlength="16" value="${esc(p.postal_code || '')}" /></label>
              <label class="stack-2"><span>${esc(t('tenants.form.city'))}</span>
                <input class="input" name="city" maxlength="128" value="${esc(p.city || '')}" /></label>
            </div>
            <label class="stack-2"><span>${esc(t('tenants.form.address_line1'))}</span>
              <input class="input" name="address_line1" maxlength="255" value="${esc(p.address_line1 || '')}" /></label>
            <label class="stack-2"><span>${esc(t('tenants.form.address_line2'))}</span>
              <input class="input" name="address_line2" maxlength="255" value="${esc(p.address_line2 || '')}" /></label>
            <label class="stack-2"><span>${esc(t('tenants.form.country'))}</span>
              ${countryFieldHtml(p.country || 'HU', { name: 'country' })}</label>

            <div class="row" style="justify-content:flex-end;gap:var(--space-3)">
              <a class="btn btn-ghost" href="#/tenants">${esc(t('tenants.form.cancel'))}</a>
              <button type="submit" class="btn btn-primary" ${this.state.saving ? 'disabled' : ''}>${esc(t('tenants.form.save'))}</button>
            </div>
          </div>
        </form>
      </section>`;
  }

  mount() {
    if (this.state.loading) {
      if (this.id && !this._loadStarted) { this._loadStarted = true; this._load(); }
      return;
    }
    const root = this.el;
    if (!root) return;

    const picker = root.querySelector('[data-entity-picker]');
    const hidden = root.querySelector('input[type="hidden"][name="entity_type"]');
    const companyFields = root.querySelector('[data-company-fields]');
    if (picker) picker.addEventListener('click', (e) => {
      const tile = e.target.closest('[data-entity-pick]');
      if (!tile) return;
      const pick = tile.getAttribute('data-entity-pick');
      this.draft.entity_type = pick;
      if (hidden) hidden.value = pick;
      picker.querySelectorAll('[data-entity-pick]').forEach((el) => {
        const isSel = el === tile;
        el.classList.toggle('is-selected', isSel);
        el.setAttribute('aria-checked', isSel ? 'true' : 'false');
      });
      if (companyFields) companyFields.hidden = pick !== 'company';
    });

    const form = root.querySelector('[data-tenant-form]');
    if (form) {
      wireAddressFields(form, { requireDefault: true });
      form.addEventListener('submit', (e) => { e.preventDefault(); this._save(form); });
    }
  }

  async _load() {
    try {
      const party = await api.get(`/tenant-parties/${this.id}`);
      this.draft = { entity_type: 'individual', ...party };
      this.setState({ loading: false });
    } catch (err) {
      this.setState({ loading: false, error: err instanceof ApiError ? err.message : t('tenants.errors.load') });
    }
  }

  async _save(form) {
    const data = Object.fromEntries(new FormData(form).entries());
    delete data.address_paste;
    if (!String(data.display_name || '').trim()) {
      this.setState({ formError: t('tenants.errors.name_required') });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      if (this.id) await api.patch(`/tenant-parties/${this.id}`, data);
      else await api.post('/tenant-parties', data);
      window.location.hash = '#/tenants';
    } catch (err) {
      this.draft = { ...this.draft, ...data };
      this.setState({ saving: false, formError: err instanceof ApiError ? err.message : t('tenants.errors.save') });
    }
  }
}
